import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Building2,
  Factory,
  Truck,
  Hotel,
  Building,
  Headphones,
  ArrowRight,
  CheckCircle,
  Database,
  FileText,
  BarChart3,
} from 'lucide-react';

const industriesData = {
  'real-estate': {
    icon: Building2,
    title: 'Real Estate & Built Environment',
    subtitle: 'Sustainability data solutions for property portfolios and building operations.',
    description: 'Comprehensive ESG data management for commercial real estate, property portfolios, and building operations with tenant engagement capabilities.',
    dataSources: [
      'Building Management Systems (BMS)',
      'Smart meters and IoT sensors',
      'Tenant utility data',
      'Waste management systems',
      'Green building certifications',
    ],
    requirements: [
      'GRESB reporting and benchmarking',
      'LEED and WELL certification data',
      'Scope 3 tenant emissions',
      'Energy Star portfolio reporting',
      'EU Taxonomy alignment',
    ],
    deployments: [
      'Portfolio-wide carbon tracking',
      'Tenant engagement platforms',
      'Asset-level benchmarking',
      'Green lease compliance',
      'Net zero pathway planning',
    ],
    outcomes: [
      { value: '30%', label: 'Energy Reduction' },
      { value: '50+', label: 'Properties Managed' },
      { value: 'GRESB', label: '5-Star Rating' },
      { value: 'Real-time', label: 'Monitoring' },
    ],
  },
  'manufacturing': {
    icon: Factory,
    title: 'Manufacturing',
    subtitle: 'Factory-grade sustainability data engineering for multi-site operations.',
    description: 'SusTek builds sustainability data pipelines for electronics and general manufacturing—automating GHG inventory, regulatory compliance, and audit-ready evidence across global operations.',
    dataSources: [
      'Production line sensors (MES, SCADA)',
      'Energy Management Systems (EMS)',
      'Material flow tracking',
      'Waste stream monitoring',
      'Supply chain data exchange',
    ],
    requirements: [
      'Scope 1/2/3 GHG inventory',
      'Product carbon footprint (PCF)',
      'CDP and customer disclosure',
      'Science-based targets tracking',
      'Circular economy metrics',
    ],
    deployments: [
      'Multi-site data consolidation',
      'Real-time emissions monitoring',
      'Supplier data integration',
      'Factory sustainability KPIs',
      'Audit-ready evidence packs',
    ],
    outcomes: [
      { value: '40%', label: 'Time Savings' },
      { value: '99.9%', label: 'Data Accuracy' },
      { value: '100+', label: 'KPIs Tracked' },
      { value: 'Multi-site', label: 'Coverage' },
    ],
  },
  'logistics': {
    icon: Truck,
    title: 'Logistics & Supply Chain',
    subtitle: 'Visibility and reporting across complex supply chain operations.',
    description: 'End-to-end sustainability data solutions for logistics providers, freight forwarders, and supply chain operators with emissions tracking and reporting.',
    dataSources: [
      'Fleet telematics and GPS',
      'Fuel consumption systems',
      'Warehouse energy meters',
      'Modal transportation data',
      'Last-mile delivery tracking',
    ],
    requirements: [
      'GLEC Framework compliance',
      'ISO 14083 reporting',
      'Customer emissions reporting',
      'Modal shift analysis',
      'Green freight certification',
    ],
    deployments: [
      'Fleet emissions tracking',
      'Route optimization analytics',
      'Warehouse sustainability KPIs',
      'Supply chain carbon mapping',
      'Customer reporting portals',
    ],
    outcomes: [
      { value: '25%', label: 'Emissions Reduction' },
      { value: 'ISO', label: 'Compliant' },
      { value: 'Real-time', label: 'Tracking' },
      { value: 'Multi-modal', label: 'Coverage' },
    ],
  },
  'hospitality': {
    icon: Hotel,
    title: 'Hospitality & Tourism',
    subtitle: 'Guest-facing sustainability programs with verified impact.',
    description: 'Sustainability data infrastructure for hotels, resorts, and tourism operators with guest engagement and certification support.',
    dataSources: [
      'Property Management Systems',
      'Energy and water meters',
      'Waste stream data',
      'F&B procurement systems',
      'Guest activity data',
    ],
    requirements: [
      'Green Key / Green Globe certification',
      'GSTC criteria alignment',
      'Carbon neutral claims',
      'Local community impact',
      'Biodiversity reporting',
    ],
    deployments: [
      'Property-level sustainability dashboards',
      'Guest carbon offset programs',
      'F&B supply chain tracking',
      'Water stewardship programs',
      'Community impact measurement',
    ],
    outcomes: [
      { value: '35%', label: 'Resource Savings' },
      { value: 'Certified', label: 'Green Properties' },
      { value: 'Guest', label: 'Engagement' },
      { value: 'Verified', label: 'Claims' },
    ],
  },
  'public-sector': {
    icon: Building,
    title: 'Public Sector / Smart Cities',
    subtitle: 'City-wide sustainability data platforms for public accountability.',
    description: 'Comprehensive sustainability data solutions for municipalities, government agencies, and smart city initiatives with public transparency features.',
    dataSources: [
      'City infrastructure systems',
      'Public transit networks',
      'Utility consumption data',
      'Waste management systems',
      'Air quality monitoring',
    ],
    requirements: [
      'GPC / C40 reporting',
      'SDG progress tracking',
      'Climate action plans',
      'Public disclosure requirements',
      'Grant and funding compliance',
    ],
    deployments: [
      'City-wide emissions inventory',
      'Public sustainability dashboards',
      'Transit sustainability tracking',
      'Smart building networks',
      'Climate resilience monitoring',
    ],
    outcomes: [
      { value: 'City-wide', label: 'Coverage' },
      { value: 'Public', label: 'Transparency' },
      { value: 'SDG', label: 'Aligned' },
      { value: 'Real-time', label: 'Reporting' },
    ],
  },
  'itbpm': {
    icon: Headphones,
    title: 'IT-BPM & Business Process Outsourcing',
    subtitle: 'Sustainability reporting and ESG compliance for the IT-BPM industry.',
    description: 'SusTek provides comprehensive sustainability data engineering and reporting solutions for the IT-BPM sector, helping BPO companies meet increasing client demands for ESG transparency, supply chain compliance, and sustainable operations.',
    heroImage: 'https://images.unsplash.com/photo-1606857521015-7f9fcf423740?w=800&q=80',
    dataSources: [
      'Facility energy management systems',
      'Workforce headcount and seat utilization data',
      'IT infrastructure power consumption',
      'Employee commute and transport data',
      'Waste and recycling tracking systems',
      'HR systems for diversity and inclusion metrics',
    ],
    requirements: [
      'Client ESG disclosure requirements',
      'Supply chain sustainability compliance',
      'GHG Protocol Scope 1, 2 & 3 reporting',
      'Double materiality assessments',
      'IBPAP baseline reporting standards',
      'Global sustainability frameworks (GRI, SASB)',
    ],
    deployments: [
      'Centralized ESG data platform',
      'Automated sustainability reporting',
      'Client-specific compliance dashboards',
      'Workforce sustainability metrics',
      'Energy efficiency monitoring',
      'Carbon footprint per FTE tracking',
    ],
    materialityIssues: [
      {
        category: 'Environmental',
        issues: [
          'Energy consumption and efficiency (kWh per seat)',
          'GHG emissions (Scope 1, 2, and 3)',
          'E-waste management and recycling',
          'Water consumption in facilities',
          'Renewable energy adoption',
          'Paper and resource consumption',
        ],
      },
      {
        category: 'Social',
        issues: [
          'Employee health, safety and well-being',
          'Mental health programs and support',
          'Diversity, equity and inclusion (DEI)',
          'Training and career development',
          'Fair wages and labor practices',
          'Community engagement and CSR',
          'Data privacy and security for employees',
        ],
      },
      {
        category: 'Governance',
        issues: [
          'ESG governance structures',
          'Business ethics and anti-corruption',
          'Client data security and privacy',
          'Regulatory compliance',
          'Supply chain due diligence',
          'Transparency in reporting',
        ],
      },
    ],
    bpoMetrics: [
      { metric: 'Energy per Seat', unit: 'kWh/seat/month', description: 'Facility energy consumption normalized by operational seats' },
      { metric: 'Carbon per FTE', unit: 'tCO2e/FTE/year', description: 'Total emissions divided by full-time equivalent employees' },
      { metric: 'Renewable Energy Mix', unit: '%', description: 'Percentage of energy from renewable sources' },
      { metric: 'Employee Turnover', unit: '%', description: 'Annual attrition rate as social sustainability indicator' },
      { metric: 'Training Hours', unit: 'hours/employee', description: 'Annual training and development investment' },
      { metric: 'Gender Diversity', unit: '%', description: 'Percentage of women in workforce and leadership' },
      { metric: 'E-waste Recycled', unit: '%', description: 'IT equipment properly recycled at end of life' },
      { metric: 'Mental Health Program', unit: 'coverage %', description: 'Employees with access to mental health support' },
    ],
    outcomes: [
      { value: '100%', label: 'Client Compliance' },
      { value: 'Baseline', label: 'Reports Generated' },
      { value: '50+', label: 'ESG Metrics Tracked' },
      { value: 'Industry', label: 'Standards Aligned' },
    ],
  },
};

export const Industries = ({ type = 'real-estate' }) => {
  const industry = industriesData[type] || industriesData['real-estate'];
  const Icon = industry.icon;
  const isITBPM = type === 'itbpm';

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-subtle">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`${isITBPM ? 'grid lg:grid-cols-2 gap-12 items-center' : 'max-w-4xl'}`}>
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-teal/10 text-teal flex items-center justify-center">
                  <Icon className="h-6 w-6" />
                </div>
                <Badge variant="outline">Industries</Badge>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-6">
                {industry.title}
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                {industry.subtitle}
              </p>
              <p className="text-lg text-muted-foreground mb-8">
                {industry.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/book-call">
                  <Button variant="teal" size="lg" className="group">
                    Book a Discovery Call
                    <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            </div>
            {isITBPM && industry.heroImage && (
              <div className="hidden lg:block">
                <img
                  src={industry.heroImage}
                  alt={industry.title}
                  className="rounded-2xl shadow-xl"
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ITBPM-specific Materiality Section */}
      {isITBPM && industry.materialityIssues && (
        <section className="py-20 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <Badge variant="outline" className="mb-4">Double Materiality</Badge>
              <h2 className="text-3xl font-bold text-foreground mb-4">
                IT-BPM Materiality Issues
              </h2>
              <p className="text-lg text-muted-foreground">
                Key ESG topics material to the IT-BPM industry, identified through double materiality assessment covering both financial impact and stakeholder importance.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {industry.materialityIssues.map((category, index) => (
                <Card key={index} className="border-border/50 bg-card">
                  <CardHeader>
                    <CardTitle className={`text-lg ${
                      category.category === 'Environmental' ? 'text-teal' :
                      category.category === 'Social' ? 'text-secondary' : 'text-accent'
                    }`}>
                      {category.category}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {category.issues.map((issue, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <CheckCircle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${
                            category.category === 'Environmental' ? 'text-teal' :
                            category.category === 'Social' ? 'text-secondary' : 'text-accent'
                          }`} />
                          {issue}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ITBPM-specific BPO Metrics Section */}
      {isITBPM && industry.bpoMetrics && (
        <section className="py-20 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <Badge variant="outline" className="mb-4">BPO Sustainability Metrics</Badge>
              <h2 className="text-3xl font-bold text-foreground mb-4">
                Industry-Specific KPIs
              </h2>
              <p className="text-lg text-muted-foreground">
                Key performance indicators tailored for compliant BPO companies measuring sustainability performance across operations.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {industry.bpoMetrics.map((item, index) => (
                <Card key={index} className="border-border/50 bg-card hover-lift">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm font-semibold text-teal mb-1">{item.metric}</p>
                      <p className="text-xs text-muted-foreground mb-3">{item.unit}</p>
                      <p className="text-xs text-muted-foreground">{item.description}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Data Sources */}
      <section className={`py-20 ${isITBPM ? 'bg-background' : 'bg-background'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Database className="h-6 w-6 text-teal" />
                <Badge variant="outline">Data Sources</Badge>
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-4">
                Typical Data Sources
              </h2>
              <ul className="space-y-3">
                {industry.dataSources.map((source, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-teal flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{source}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-4">
                <FileText className="h-6 w-6 text-teal" />
                <Badge variant="outline">Requirements</Badge>
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-4">
                Reporting Requirements
              </h2>
              <ul className="space-y-3">
                {industry.requirements.map((req, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-teal flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{req}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-4">
                <BarChart3 className="h-6 w-6 text-teal" />
                <Badge variant="outline">Deployments</Badge>
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-4">
                SusTek Deployments
              </h2>
              <ul className="space-y-3">
                {industry.deployments.map((deployment, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-teal flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{deployment}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">Results</Badge>
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Typical Outcomes
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {industry.outcomes.map((outcome, index) => (
              <Card key={index} className="text-center border-border/50 bg-card">
                <CardContent className="pt-6">
                  <p className="text-3xl sm:text-4xl font-bold text-teal mb-2">{outcome.value}</p>
                  <p className="text-muted-foreground">{outcome.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-primary text-primary-foreground border-0">
            <CardContent className="p-8 lg:p-12">
              <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold mb-3">
                    Ready to Transform Your {industry.title.split(' ')[0]} Sustainability Program?
                  </h2>
                  <p className="text-lg text-primary-foreground/80">
                    Let us discuss how SusTek can help your organization.
                  </p>
                </div>
                <Link to="/book-call">
                  <Button variant="secondary" size="lg" className="group text-secondary-foreground">
                    Book a Discovery Call
                    <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Industries;
